import React from 'react';
import {
  Box,
  Menu,
  MenuItem,
  Button,
  Chip,
  Divider,
  useTheme,
  Stack,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import {
  PhoneAndroid as PhoneIcon,
  Settings as SettingsIcon,
  ScreenRotation as RotationIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useMenu } from '../../../components/base/useMenu';
import CustomScrollbar from '../../TestPage/components/CustomScrollbar';
import { useParams } from 'react-router-dom';
import TestDetails from './TestDetails';
import EmailMenu from '../../TestPage/components/EmailMenu';

const DeviceConfig = ({
  platformDropdownOptions,
  selectedDevice,
  setSelectedDevice,
  osVersion,
  setOsVersion,
  orientation,
  setOrientation,
  updateConfig,
  sessionActive,
  session,
  activeFolder,
  name,
  client,
  testResponse
}) => {
  const { triggerProps: deviceTriggerProps, menuProps: deviceMenuProps } = useMenu();
  const { triggerProps: orientationTriggerProps, menuProps: orientationMenuProps } = useMenu();
  const { triggerProps: osTriggerProps, menuProps: osMenuProps } = useMenu();
  const { batchId } = useParams();
  const theme = useTheme();

  const handleDeviceChange = async (device) => {
    setSelectedDevice(device);
    const activeOs = device.os.find((os) => os === osVersion);
    setOsVersion(activeOs ? activeOs : device.os[device.os.length - 1]);

    const makeDeviceSelection = async () => {
      await updateConfig(
        {},
        {
          device: device.device_identifier,
          osVersion: activeOs ? activeOs : device.os[device.os.length - 1],
          centered: 'both',
          scale: 'auto'
        }
      );
    };

    if (sessionActive) {
      const confirmChange = window.confirm('This will end your current session. Are you sure?');
      if (confirmChange) makeDeviceSelection();
    } else {
      makeDeviceSelection();
    }
    deviceMenuProps.onClose();
  };

  const handleOsChange = async (os) => {
    setOsVersion(os);
    const makeOsChange = async () => {
      await updateConfig({}, { osVersion: os });
    };

    if (sessionActive) {
      const confirmChange = window.confirm('This will end your current session. Are you sure?');
      if (confirmChange) makeOsChange();
    } else {
      makeOsChange();
    }
    osMenuProps.onClose();
  };

  const handleOrientationChange = async (_orientation) => {
    setOrientation(_orientation);
    await updateConfig({}, { orientation: _orientation });
    orientationMenuProps.onClose();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        // border: `1px solid ${theme.palette.divider}`,
        // boxShadow: theme.shadows[0.5]
      }}>
      {/* Device Selector */}
      <Tooltip title="Select device">
        <Button
          variant="outlined"
          size="small"
          disabled={!!batchId}
          startIcon={<PhoneIcon fontSize="small" />}
          endIcon={deviceMenuProps.open ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
          {...deviceTriggerProps}
          sx={{
            minWidth: 180,
            justifyContent: 'space-between',
            textTransform: 'none',
            py: 1,
            px: 2
          }}>
          <Typography variant="body2" noWrap>
            {selectedDevice.device}
          </Typography>
        </Button>
      </Tooltip>

      <Menu
        {...deviceMenuProps}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 250,
            maxHeight: 300,
            mt: 1
          }
        }}>
        <CustomScrollbar>
          {platformDropdownOptions.map((device, index) => (
            <MenuItem
              key={index}
              onClick={() => handleDeviceChange(device)}
              selected={device.device_identifier === selectedDevice.device_identifier}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected
                }
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{device.device}</Typography>
                <Chip label={device.os_type} size="small" sx={{ ml: 'auto' }} />
              </Box>
            </MenuItem>
          ))}
        </CustomScrollbar>
      </Menu>

      {/* OS Version Selector */}
      <Tooltip title="Select OS version">
        <Button
          variant="outlined"
          size="small"
          disabled={!!batchId}
          startIcon={<SettingsIcon fontSize="small" />}
          endIcon={osMenuProps.open ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
          {...osTriggerProps}
          sx={{
            minWidth: 140,
            justifyContent: 'space-between',
            textTransform: 'none',
            py: 1,
            px: 2
          }}>
          <Typography variant="body2" noWrap>
            {selectedDevice.os_type} {osVersion}
          </Typography>
        </Button>
      </Tooltip>

      <Menu
        {...osMenuProps}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 200,
            maxHeight: 300,
            mt: 1
          }
        }}>
        {selectedDevice.os.map((os, index) => (
          <MenuItem
            key={index}
            onClick={() => handleOsChange(os)}
            selected={os === osVersion}
            sx={{
              '&.Mui-selected': {
                backgroundColor: theme.palette.action.selected
              }
            }}>
            <Typography variant="body2">
              {selectedDevice.os_type} {os.toString()}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Orientation Selector */}
      <Tooltip title="Select orientation">
        <Button
          variant="outlined"
          size="small"
          disabled={!!batchId}
          startIcon={<RotationIcon fontSize="small" />}
          endIcon={orientationMenuProps.open ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
          {...orientationTriggerProps}
          sx={{
            minWidth: 120,
            justifyContent: 'space-between',
            textTransform: 'none',
            py: 1,
            px: 2
          }}>
          <Typography variant="body2" noWrap>
            {orientation}
          </Typography>
        </Button>
      </Tooltip>

      <Menu
        {...orientationMenuProps}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 150,
            mt: 1
          }
        }}>
        <MenuItem
          onClick={() => handleOrientationChange('portrait')}
          selected={orientation === 'portrait'}>
          Portrait
        </MenuItem>
        <MenuItem
          onClick={() => handleOrientationChange('landscape')}
          selected={orientation === 'landscape'}>
          Landscape
        </MenuItem>
      </Menu>

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      {/* Action Buttons */}
      <Stack direction="row" spacing={1}>
        <EmailMenu theme={theme} />

        <Tooltip title="Test details">
          <span>
            <TestDetails
              activeFolder={activeFolder}
              name={name}
              appetizeClient={client}
              testResponse={testResponse}
              trigger={
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              }
            />
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default DeviceConfig;
